import plur from 'plur';
import { format0 } from 'utils/number';

/**
 * The pager's "1–100 of 2,140 wells".
 *
 * Carbon writes its own counts out unseparated, and a corridor across a basin can hold
 * five figures of wells — "23140" is not a readable number.
 *
 * @param {number} min - First row on the page.
 * @param {number} max - Last row on the page.
 * @param {number} total - Rows in the whole result.
 * @return {string} The range.
 */
export const itemRangeText = (
  min: number,
  max: number,
  total: number
): string => {
  return `${format0(min)}–${format0(max)} of ${format0(total)} ${plur(
    'well',
    total
  )}`;
};

/**
 * The pager's "of 22 pages", separated for the same reason.
 *
 * @param {number} _current - The page shown, which the text does not repeat.
 * @param {number} total - How many pages there are.
 * @return {string} The count.
 */
export const pageRangeText = (_current: number, total: number): string => {
  return `of ${format0(total)} ${plur('page', total)}`;
};

/**
 * A well's distance from the section line, as the picker reads it.
 *
 * @param {number | null} offsetFt - The distance in feet.
 * @return {string} The text, or an unknown marker where the source cannot measure it.
 */
export const fromLineText = (offsetFt: number | null): string => {
  return offsetFt === null ? '—' : `${format0(offsetFt)} ft`;
};

/**
 * A well's total depth, survey-resolved on the backend (surface minus the toe's
 * TVDSS). True vertical rather than measured depth, so it is labelled neither MD nor
 * plain "depth".
 *
 * @param {number | null} totalDepthFt - The depth in feet.
 * @return {string} The text.
 */
export const totalDepthText = (totalDepthFt: number | null): string => {
  return totalDepthFt === null ? '—' : `${format0(totalDepthFt)} ft`;
};
